import Cross from "@icons/cross.svg?react";
import { Trans } from "@lingui/react/macro";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import invariant from "tiny-invariant";

import {
  canSignCheckFn,
  useGuard,
} from "@/common/acl/action-guard/action-guard";
import { Button } from "@/common/components/button/button";
import { Dialog, useDialogProvider } from "@/common/components/dialog/dialog";
import { Failed } from "@/common/components/info/info";
import { Fields, OrderForm } from "@/common/components/order-form/order-form";
import { PropertiesList } from "@/common/components/properties-list/properties-list";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spacer } from "@/common/components/spacer/spacer";
import { Spinner } from "@/common/components/spinner/spinner";
import { useLanguage } from "@/common/hooks/use-language";

import { FullscreenModalLayout } from "../layout/layout.fullscreen";
import { handleFormError } from "../shared/api/error-handler";
import { useRemoveOrderMutation } from "../shared/api/remove-order";
import { path as failedPath } from "../shared/routes/failed/failed";
import { path as successPath } from "../shared/routes/success/success";
import { orderDetailQuery, useEditOrderMutation } from "./orders.api";

const path = "offers/:offerId/orders/:orderId";
const Order = () => {
  const [searchParams] = useSearchParams();
  const { offerId, orderId } = useParams();
  invariant(offerId);
  invariant(orderId);
  const locale = useLanguage();
  const order = useQuery(
    orderDetailQuery({
      locale,
      offerId,
      orderId,
    }),
  );

  const queryClient = useQueryClient();
  const {
    closeDialog: closeConfirmationDialog,
    openDialog: openConfirmationDialog,
  } = useDialogProvider();
  const removeOrder = useRemoveOrderMutation();
  const editOrder = useEditOrderMutation();
  const navigate = useNavigate();
  const handleSubmit = (data: Fields) => {
    const existing = order.data;
    invariant(existing);

    editOrder.mutate(
      {
        id: orderId,
        order: {
          active: true,
          amount: data.tons,
          currency: order.data.order.currency,
          customTransport: data.customTransport,
          lowPrice: data.lowPricePerTon,
          offer: offerId,
          price: data.pricePerTon,
          quarter: data.quarter,
          transportNote: data.customTransportNote,
        },
      },
      {
        onError: async (e) => {
          const errors = handleFormError(e);
          await navigate(failedPath + "?" + searchParams.toString(), {
            state: { errors },
          });
        },
        onSuccess: async () => {
          await queryClient.invalidateQueries();
          await navigate(successPath);
        },
      },
    );
  };
  const guardedSubmit = useGuard({
    action: handleSubmit,
    checkFn: canSignCheckFn,
  });

  const handleRemoveOrder = () => {
    closeConfirmationDialog();
    removeOrder.mutate(
      { orderId },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries();
          await navigate("..");
        },
      },
    );
  };

  const ConfirmationDialog = (
    <Dialog>
      <Dialog.Title>
        <Trans>Chcete příkaz opravdu smazat?</Trans>
      </Dialog.Title>
      <Dialog.Actions>
        <Dialog.Confirm onClick={handleRemoveOrder}>
          <Trans>Ano</Trans>
        </Dialog.Confirm>
        <Dialog.Cancel onClick={closeConfirmationDialog}>
          <Trans>Ne</Trans>
        </Dialog.Cancel>
      </Dialog.Actions>
    </Dialog>
  );

  return (
    <>
      <FullscreenModalLayout
        headerButton={
          <SearchParamsLink to="..">
            <Cross />
          </SearchParamsLink>
        }
      >
        <FullscreenModalLayout.Title>
          <Trans>Detail příkazu</Trans>
        </FullscreenModalLayout.Title>
        {order.status === "pending" ? <Spinner className="m-auto" /> : null}
        {order.status === "error" ? <Failed error={order.error} /> : null}
        {order.status === "success" ? (
          <>
            <PropertiesList
              properties={[
                {
                  label: <Trans>Možný prodej celkem za</Trans>,
                  value: order.data.order.totalPrice,
                },
                {
                  label: <Trans>Komodita</Trans>,
                  value: order.data.crop.name,
                },
                {
                  label: <Trans>Farma</Trans>,
                  value: order.data.company.name,
                },
                {
                  label: <Trans>Sklad</Trans>,
                  value: order.data.storage.name,
                },
              ]}
            />
            <Spacer className="h-6" />
            <OrderForm
              initialValues={{
                customTransport: order.data.order.customTransport,
                customTransportNote: order.data.order.customTransportNote,
                hasLowPrice: !!order.data.order.unitLowPrice,
                lowPricePerTon: order.data.order.unitLowPrice,
                note: order.data.order.note,
                pricePerTon: order.data.order.unitPrice,
                quarter: order.data.quarter.id,
                tons: order.data.order.amount,
                trader: order.data.order.trader?.id,
              }}
              key={orderId}
              onSubmit={guardedSubmit}
            >
              <OrderForm.QuarterField />
              <OrderForm.LowPriceField currency={order.data.order.currency} />
              <OrderForm.PriceField currency={order.data.order.currency} />
              <OrderForm.TraderField>
                {/* TODO : add all traders */}
                <option value="ALL">
                  <Trans>Všichni obchodníci</Trans>
                </option>
                {order.data.order.trader?.id ? (
                  <option
                    key={order.data.order.trader.id}
                    value={order.data.order.trader.id}
                  >
                    {order.data.order.trader.name}
                  </option>
                ) : null}
              </OrderForm.TraderField>
              <OrderForm.AmountField />
              <OrderForm.NoteField />
              <OrderForm.AmountField />
              <OrderForm.CustomTransportField />
              <OrderForm.NoteField />
            </OrderForm>
            <Button.Container>
              <Button.Delete
                onClick={() => openConfirmationDialog(ConfirmationDialog)}
                type="button"
              >
                <Trans>Smazat příkaz</Trans>
              </Button.Delete>
              <Button
                disabled={editOrder.status === "pending"}
                form={OrderForm.formId}
                type="submit"
                variant="primary"
              >
                <Trans>Uložit</Trans>
              </Button>
            </Button.Container>
          </>
        ) : null}
      </FullscreenModalLayout>
      <Outlet />
    </>
  );
};

export { Order, path };
